import type { ReactElement } from 'react';
import type { User } from '../api';
import { Sheet } from './Sheet';

/**
 * Who may do what, written down from the rules the server enforces. Two
 * layers: the account (admin or member) and the role in a project (owner,
 * editor, viewer, guest link). Nothing here is a setting -- settings live
 * in "Konten" and in each project's "Zusammenarbeiten".
 */

const ACCOUNT: [string, boolean, boolean][] = [
  ['Projekte anlegen und eigene besitzen', true, true],
  ['In fremden Projekten mitarbeiten, wenn eingeladen', true, true],
  ['Konten anlegen, sperren, Rollen vergeben', true, false],
  ['Ablage, Mail, Speicher, Diagnose einrichten', true, false],
  ['Marken-Kits aller Projekte verwalten', true, false],
];

const PROJECT: [string, boolean, boolean, boolean, boolean][] = [
  ['Projekt ansehen und Vorschau abspielen', true, true, true, true],
  ['Schneiden: Clips, Text, Ton, Untertitel', true, true, false, true],
  ['Material hochladen und aus der Ablage holen', true, true, false, false],
  ['Exportieren', true, true, false, true],
  ['Mitglieder und Gast-Links verwalten', true, false, false, false],
  ['Umbenennen, Format, Ordner, Archiv', true, false, false, false],
  ['Projekt löschen', true, false, false, false],
];

function Mark({ yes }: { yes: boolean }): ReactElement {
  return <span className={`badge ${yes ? 'ok' : ''}`} style={{ minWidth: 28, textAlign: 'center' }}>{yes ? 'ja' : '–'}</span>;
}

export function RightsSheet({ user, onClose }: { user: User; onClose: () => void }): ReactElement {
  return (
    <Sheet title="Wer darf was" onClose={onClose}>
      <p className="hint" style={{ marginTop: 0 }}>
        Du bist {user.role === 'admin' ? 'Admin' : 'Mitglied'}. Zwei Ebenen: das Konto, und die Rolle je Projekt.
      </p>
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Konto</h3>
      <div className="rights-table" role="table">
        <div className="rights-head" role="row">
          <span />
          <span>Admin</span>
          <span>Mitglied</span>
        </div>
        {ACCOUNT.map(([what, admin, member]) => (
          <div className="rights-row" role="row" key={what}>
            <span>{what}</span>
            <Mark yes={admin} />
            <Mark yes={member} />
          </div>
        ))}
      </div>
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Im Projekt</h3>
      <div className="rights-table wide" role="table">
        <div className="rights-head" role="row">
          <span />
          <span>Eigentümerin</span>
          <span>Bearbeiter</span>
          <span>Betrachter</span>
          <span>Gast-Link (Mitschneiden)</span>
        </div>
        {PROJECT.map(([what, owner, editor, viewer, guest]) => (
          <div className="rights-row" role="row" key={what}>
            <span>{what}</span>
            <Mark yes={owner} />
            <Mark yes={editor} />
            <Mark yes={viewer} />
            <Mark yes={guest} />
          </div>
        ))}
      </div>
      <p className="hint">
        Rollen im Projekt vergibt die Eigentümerin unter „Zusammenarbeiten“, Konten ein Admin unter
        „Verwaltung → Konten“.
      </p>
    </Sheet>
  );
}
